import { ListChecks } from "lucide-react";
import {
  estComplete,
  grouper,
  SECTIONS,
  type Dimension,
  type Etat,
} from "./fourchettes-modele";

type Props = {
  dimensions: Dimension[];
  etat: Etat;
  /** Index de la dimension affichée ; `dimensions.length` = récapitulatif. */
  position: number;
  onAller: (position: number) => void;
};

/**
 * Compteur et pastilles du carrousel : une pastille par dimension, groupées
 * par section, pleine quand la dimension est renseignée. Chacune mène
 * directement à sa dimension — le carrousel ne montre qu'une carte, c'est ce
 * qui garde la vue d'ensemble. Le récapitulatif ferme la rangée.
 *
 * La zone cliquable (24 px) déborde la pastille visible (10 px) : un point de
 * 10 px se rate au doigt.
 */
export function NavigationDimensions({ dimensions, etat, position, onAller }: Props) {
  const total = dimensions.length;
  const completes = dimensions.filter((d) => estComplete(etat[d.code])).length;
  const surRecapitulatif = position === total;

  return (
    <nav aria-label="Dimensions du référentiel" className="space-y-2">
      <div className="flex items-center gap-3">
        <p className="shrink-0 text-sm font-medium tabular-nums text-charte-strong">
          {completes}/{total} dimension{total > 1 ? "s" : ""} renseignée
          {completes > 1 ? "s" : ""}
        </p>
        <div aria-hidden className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
            style={{ width: `${(completes / Math.max(1, total)) * 100}%` }}
          />
        </div>
      </div>

      <ol className="flex flex-wrap items-center gap-y-1">
        {grouper(dimensions).map(([categorie, membres], rang) => (
          <li
            key={categorie || "sans-categorie"}
            className={`flex flex-wrap items-center ${
              rang > 0 ? "ml-2 border-l border-charte-line-strong pl-2" : ""
            }`}
          >
            <ol
              aria-label={SECTIONS[categorie]?.titre}
              className="flex flex-wrap items-center"
            >
              {membres.map(({ dimension, index }) => {
                const complete = estComplete(etat[dimension.code]);
                const courante = index === position;
                return (
                  <li key={dimension.code}>
                    <button
                      type="button"
                      onClick={() => onAller(index)}
                      aria-current={courante ? "step" : undefined}
                      aria-label={`${dimension.label} — ${complete ? "renseignée" : "à renseigner"}`}
                      title={dimension.label}
                      className="group grid h-6 w-6 cursor-pointer place-items-center rounded-full focus-visible:outline-2 focus-visible:outline-primary"
                    >
                      <span
                        className={`block rounded-full transition-all duration-150 ${
                          courante ? "h-3 w-3 ring-2 ring-primary ring-offset-2" : "h-2.5 w-2.5"
                        } ${
                          complete
                            ? "bg-primary"
                            : "border border-charte-line-loud bg-card group-hover:border-primary"
                        }`}
                      />
                    </button>
                  </li>
                );
              })}
            </ol>
          </li>
        ))}
        <li className="ml-2 border-l border-charte-line-strong pl-2">
          <button
            type="button"
            onClick={() => onAller(total)}
            aria-current={surRecapitulatif ? "step" : undefined}
            className={`inline-flex h-6 cursor-pointer items-center gap-1.5 rounded-pastille px-2 text-xs font-medium transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-primary ${
              surRecapitulatif
                ? "bg-primary text-primary-foreground"
                : "text-charte-muted hover:bg-charte-hover hover:text-charte-strong"
            }`}
          >
            <ListChecks aria-hidden className="h-3.5 w-3.5" />
            Récapitulatif
          </button>
        </li>
      </ol>
    </nav>
  );
}
